import { api } from "./api";
import { idb } from "./idb";

const Q_KEY_LEGACY = "piche_offline_queue";
const C_KEY = "piche_cache:";
const listeners = new Set();
let memQueue = [];

export const isOnline = () => navigator.onLine !== false;
export function onQueueChange(fn) { listeners.add(fn); fn(memQueue); return () => listeners.delete(fn); }
const emit = () => listeners.forEach((fn) => fn(memQueue));
export function getQueue() { return memQueue; }

async function loadQueue() {
  if (idb.supported) {
    memQueue = await idb.all("queue").catch(() => []);
    const legacy = localStorage.getItem(Q_KEY_LEGACY);
    if (legacy) {
      for (const rep of JSON.parse(legacy)) await idb.set("queue", rep.client_id, rep);
      localStorage.removeItem(Q_KEY_LEGACY);
      memQueue = await idb.all("queue");
    }
  } else {
    try { memQueue = JSON.parse(localStorage.getItem(Q_KEY_LEGACY) || "[]"); } catch { memQueue = []; }
  }
  emit();
}
async function persist(rep, remove = false) {
  if (idb.supported) { remove ? await idb.del("queue", rep.client_id) : await idb.set("queue", rep.client_id, rep); }
  else localStorage.setItem(Q_KEY_LEGACY, JSON.stringify(remove ? memQueue.filter((x) => x.client_id !== rep.client_id) : [...memQueue, rep]));
  memQueue = remove ? memQueue.filter((x) => x.client_id !== rep.client_id) : [...memQueue.filter((x) => x.client_id !== rep.client_id), rep];
  emit();
}

export async function enqueueReport(report) {
  const rep = { ...report, client_id: report.client_id || `c_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`, queued_at: new Date().toISOString() };
  await persist(rep);
  try { const reg = await navigator.serviceWorker?.ready; await reg?.sync?.register("piche-sync"); } catch {}
  return rep;
}

export async function cachedGet(url) {
  try {
    const { data } = await api.get(url);
    if (idb.supported) idb.set("cache", url, data).catch(() => {}); else localStorage.setItem(C_KEY + url, JSON.stringify(data));
    return data;
  } catch (e) {
    const c = idb.supported ? await idb.get("cache", url) : JSON.parse(localStorage.getItem(C_KEY + url) || "null");
    if (c) return c;
    throw e;
  }
}

export const fileToDataUrl = (file) => new Promise((res, rej) => {
  const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(file);
});
async function toBlob(p) { return p.blob instanceof Blob ? p.blob : (await fetch(p.data)).blob(); }

let syncing = false;
export async function syncQueue() {
  if (syncing || !isOnline() || memQueue.length === 0) return { sent: 0 };
  syncing = true;
  let sent = 0;
  try {
    for (const rep of [...memQueue]) {
      try {
        const items = [];
        for (const it of rep.items) {
          const photo_ids = [...(it.photo_ids || [])];
          for (const p of it.pending_photos || []) {
            const fd = new FormData();
            fd.append("file", await toBlob(p), p.name || "photo.jpg");
            fd.append("entity_type", "report"); fd.append("phase", "during");
            const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
            photo_ids.push(data.id);
          }
          const { pending_photos, _name, _unit, ...rest } = it;
          items.push({ ...rest, photo_ids });
        }
        const payload = { project_id: rep.project_id, date: rep.date, work_date: rep.work_date || rep.date, client_id: rep.client_id, items, custom_fields: {}, gps: null, signature_file_id: null };
        if (rep.employee_id) { payload.employee_id = rep.employee_id; payload.created_for_reason = rep.created_for_reason; }
        if (rep.shift) payload.shift = rep.shift;
        for (let i = 0; i < (rep.extra_fields || []).length; i++) {
          const f = rep.extra_fields[i], k = f.id || String(i), v = (rep.custom_fields || {})[k];
          if (v === undefined || v === "") continue;
          if (f.type === "signature" && typeof v === "string" && v.startsWith("data:")) {
            const fd = new FormData(); fd.append("file", await (await fetch(v)).blob(), "signature.png"); fd.append("entity_type", "report"); fd.append("phase", "signature");
            const { data: up } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
            payload.signature_file_id = up.id; payload.custom_fields[k] = up.id;
          } else if (f.type === "gps") { payload.gps = v; payload.custom_fields[k] = `${v.lat},${v.lng}`; }
          else if (f.type === "photo" || f.type === "file") payload.custom_fields[k] = v.id;
          else payload.custom_fields[k] = v;
        }
        const { data } = await api.post("/reports", payload);
        if (data.status === "draft") await api.post(`/reports/${data.id}/submit`);
        await persist(rep, true);
        sent++;
      } catch (e) {
        if (!e.response) break;
        if (e.response.status >= 400 && e.response.status < 500 && e.response.status !== 401) await persist(rep, true);
      }
    }
  } finally { syncing = false; }
  return { sent };
}

export function initOfflineSync(onSynced) {
  const run = async () => { const r = await syncQueue(); if (r.sent && onSynced) onSynced(r.sent); };
  loadQueue().then(run);
  window.addEventListener("online", run);
  const onMsg = (e) => { if (e.data?.type === "piche-sync") run(); };
  navigator.serviceWorker?.addEventListener?.("message", onMsg);
  const iv = setInterval(run, 30000);
  return () => { window.removeEventListener("online", run); navigator.serviceWorker?.removeEventListener?.("message", onMsg); clearInterval(iv); };
}
